/**
 * OverviewPage — the live "Pronto Soccorso ora" home screen.
 *
 * Splits into:
 *   - `OverviewView`  — pure, prop-driven; renders loading / error / data.
 *     Every state is trivially testable without a network or a query client.
 *   - `OverviewPage`  — the connected default export; wires the generated
 *     TanStack Query hooks, polls the live feed, and owns the (persisted) sort.
 */

import { useState } from "react";
import { useGetLiveOverview } from "@/api/generated/hospitals/hospitals";
import { useGetRegionTrend } from "@/api/generated/region/region";
import { useGetCollectorStatus } from "@/api/generated/status/status";
import type { CollectorStatusResponse, LiveOverviewResponse } from "@/api/generated/model";
import type { RegionTrendResponse } from "@/api/generated/model/regionTrendResponse";
import { rankHospitals, type SortDirection } from "@/lib/congestion";
import { regionalBeatDuration, beatDurationStyle } from "@/lib/busyness";
import { regionTrend } from "@/lib/regionTrend";
import { readOfDay } from "@/lib/readOfDay";
import { useCountUp } from "@/lib/countUp";
import { isMorphing } from "@/lib/morph";
import { collectorSummary } from "@/lib/status";
import { overviewMeasuredAt } from "@/lib/overview";
import { HospitalCard } from "@/components/HospitalCard";
import { FreshnessBadge } from "@/components/FreshnessBadge";
import { StaleDataBanner } from "@/components/StaleDataBanner";
import { TriageLegend } from "@/components/TriageLegend";
import { HeartMark } from "@/components/HeartMark";
import { RegionTrendChartCard } from "@/components/RegionTrendChart";
import { HospitalMap } from "@/components/HospitalMap";

// ── Constants ─────────────────────────────────────────────────────────────────

/** How often to re-poll the live overview (the feed refreshes every ~5 min). */
const REFETCH_INTERVAL_MS = 60_000;
/** Number of hospitals in the seeded universe — sizes the loading skeleton. */
const SKELETON_COUNT = 7;
/** localStorage key persisting the user's chosen sort direction across visits. */
const SORT_STORAGE_KEY = "pspulse.overview.sort";
// Busiest-first by default: most people watch the two big hospitals (Bolzano,
// Merano), which sit at the bottom under calmest-first — so lead with the long
// queues rather than burying them (Lisa feedback, 2026-07).
const DEFAULT_SORT: SortDirection = "desc";

// ── i18n strings (all user-visible copy in one place) ─────────────────────────

const STRINGS = {
  eyebrow: "Pronto Soccorso",
  /** "oggi HH:MM" — %s is replaced with the formatted time */
  eyebrowTime: (timeLabel: string) => `oggi ${timeLabel}`,
  h1: ["Il battito dei pronto soccorso dell'", "Alto Adige", "."] as const,
  heroUnit: "persone",
  heroLabel: "persone in attesa nella regione",
  trendPill: {
    up: "▲ sul tipico",
    down: "▼ sul tipico",
    norm: "■ nella norma",
  },
  gridEyebrow: "PER OSPEDALE",
  gridH2: "I sette pronto soccorso, adesso",
  sortLabel: "Ordina:",
  sortAsc: "Coda più breve",
  sortDesc: "Coda più lunga",
  error: "Impossibile caricare i dati in questo momento.",
  retry: "Riprova",
} as const;

// ── Types ─────────────────────────────────────────────────────────────────────

interface OverviewViewProps {
  data: LiveOverviewResponse | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Chosen display order; defaults to busiest-first. */
  sort?: SortDirection;
  onSortChange?: (sort: SortDirection) => void;
  /** Injectable clock for deterministic tests. */
  now?: Date | undefined;
  /** Regional trend data for the oggi-vs-tipico chart. */
  trendData?: RegionTrendResponse | undefined;
  trendPending?: boolean;
  trendError?: boolean;
  /** Whether the map section is shown (can be disabled in tests). */
  showMap?: boolean;
  /** Collector health, for the stale-data banner; `undefined` while it loads. */
  collectorStatus?: CollectorStatusResponse | undefined;
}

// ── Hero helpers ──────────────────────────────────────────────────────────────

function currentTimeLabel(now: Date | undefined): string {
  const d = now ?? new Date();
  return new Intl.DateTimeFormat("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  }).format(d);
}

function trendPillData(
  trendData: RegionTrendResponse | undefined,
): { label: string; pct: number | null; direction: "up" | "down" | "norm" } | null {
  if (!trendData) return null;

  const { direction, pct } = regionTrend(trendData.today, trendData.typical);
  if (direction === "noData") return null;

  if (direction === "up" && pct !== null) {
    return { label: `▲ +${pct}% ${STRINGS.trendPill.up}`, pct, direction: "up" };
  }
  if (direction === "down" && pct !== null) {
    return { label: `▼ ${pct}% ${STRINGS.trendPill.down}`, pct, direction: "down" };
  }
  return { label: `■ ${STRINGS.trendPill.norm}`, pct, direction: "norm" };
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonHero() {
  return (
    <div className="hero-skeleton" aria-hidden="true">
      <div className="hero__text">
        <div className="skeleton-block" style={{ height: 14, width: "40%", marginBottom: 8 }} />
        <div className="skeleton-block" style={{ height: 56, width: "90%", marginBottom: 8 }} />
        <div className="skeleton-block" style={{ height: 18, width: "70%", marginBottom: 8 }} />
        <div className="skeleton-block" style={{ height: 64, width: "60%" }} />
      </div>
      <div className="region-chart__skeleton" />
    </div>
  );
}

function SkeletonGrid() {
  return (
    <ol className="overview__grid" aria-hidden="true">
      {Array.from({ length: SKELETON_COUNT }, (_, i) => (
        <li key={i}>
          <div className="hospital-card hospital-card--skeleton" />
        </li>
      ))}
    </ol>
  );
}

// ── Sort control ──────────────────────────────────────────────────────────────

function SortControl({
  value,
  onChange,
}: {
  value: SortDirection;
  onChange?: ((sort: SortDirection) => void) | undefined;
}) {
  return (
    <div className="sort-control" role="group" aria-label="Ordina i Pronto Soccorso">
      <span className="sort-control__label" aria-hidden="true">
        {STRINGS.sortLabel}
      </span>
      <button
        type="button"
        className="sort-control__btn"
        aria-pressed={value === "asc"}
        onClick={() => onChange?.("asc")}
      >
        {STRINGS.sortAsc}
      </button>
      <button
        type="button"
        className="sort-control__btn"
        aria-pressed={value === "desc"}
        onClick={() => onChange?.("desc")}
      >
        {STRINGS.sortDesc}
      </button>
    </div>
  );
}

// ── OverviewView ───────────────────────────────────────────────────────────────

export function OverviewView({
  data,
  isPending,
  isError,
  onRetry,
  sort = DEFAULT_SORT,
  onSortChange,
  now,
  trendData,
  trendPending = false,
  trendError = false,
  showMap = true,
  collectorStatus,
}: OverviewViewProps) {
  const regionalTotal =
    data?.hospitals.reduce((sum, h) => sum + (h.hasData ? h.totalWaiting : 0), 0) ?? 0;

  // When this overview was re-mounted BY a back-morph, skip the count-up and the
  // section rise-ins — the morph is the reveal (frozen at mount; false on a
  // direct load/reload, where the count-up plays as before).
  const [arrivedViaMorph] = useState(() => isMorphing());
  const countUpValue = useCountUp(regionalTotal, 1100, arrivedViaMorph);
  const beatDur = regionalBeatDuration(regionalTotal);
  const revealClass = arrivedViaMorph ? "" : " reveal";

  // The "read of the day" — the one editorial line; carries the rhythm/time
  // story the pill can't (see lib/readOfDay). The pill still owns the quantified
  // vs-typical glance below.
  const read = readOfDay(trendData, now ?? new Date());
  const pill = trendPillData(trendData);
  const timeLabel = currentTimeLabel(now);

  const rankedHospitals = data ? rankHospitals(data.hospitals, sort) : [];

  // Collector health → the non-blocking stale banner above the hero. Undefined
  // (status still loading, or its own query errored) ⇒ `unknown` ⇒ hidden.
  const collector = collectorStatus ? collectorSummary(collectorStatus) : undefined;

  return (
    <main className="overview" aria-busy={isPending}>
      {/* ── Stale-data banner (non-blocking; live data still renders below) ── */}
      <StaleDataBanner
        level={collector?.level ?? "unknown"}
        ageLabel={collector?.ageLabel ?? null}
      />

      {/* ── Hero ── (nulled on error: no misleading "0 persone" hero — see §B) */}
      {isPending ? (
        <SkeletonHero />
      ) : isError ? null : (
        <section className={`hero${revealClass}`} style={{ animationDelay: "0s" }}>
          {/* hero__text comes first in DOM (text-first on mobile — M3) */}
          <div className="hero__text">
            <p className="hero__eyebrow">
              <span className="live-mark-wrap live-ring">
                <HeartMark size={9} className="heart-mark" />
              </span>
              {STRINGS.eyebrow} · {STRINGS.eyebrowTime(timeLabel)}
            </p>

            <h1 className="hero__h1">
              {STRINGS.h1[0]}
              <em>{STRINGS.h1[1]}</em>
              {STRINGS.h1[2]}
            </h1>

            <p className="hero__lede">
              {read.segments.map((seg, i) =>
                seg.accent ? (
                  <em key={i} className="hero__lede-accent">
                    {seg.text}
                  </em>
                ) : (
                  <span key={i}>{seg.text}</span>
                ),
              )}
            </p>

            <div className="hero__metric">
              {/* Stable accessible name carries the real total; the count-up digits
                  animate and so are hidden from AT (otherwise a mid-animation value leaks).
                  role="img" gives the aria-label a valid host — a bare <div> can't carry
                  a name (aria-prohibited-attr) — and exposes the whole cluster as one figure. */}
              <div
                className="hero__big"
                role="img"
                aria-label={`${regionalTotal} ${STRINGS.heroLabel}`}
              >
                <span className="live-mark-wrap live-ring" style={beatDurationStyle(beatDur)}>
                  <HeartMark size={36} className="heart-mark" />
                </span>
                <span className="hero__number serif tnum" aria-hidden="true">
                  {countUpValue}
                </span>
                <span className="hero__unit" aria-hidden="true">
                  {STRINGS.heroUnit}
                </span>
              </div>
              <div className="hero__meta">
                <span className="hero__label">{STRINGS.heroLabel}</span>
                {pill !== null && (
                  <span className={`trend-pill trend-pill--${pill.direction}`}>{pill.label}</span>
                )}
              </div>
            </div>
          </div>

          {/* hero__chart is second in DOM; CSS order puts it visually right on desktop,
              and below text on mobile (default block flow — no order:-1 here). */}
          <div className="hero__chart">
            <RegionTrendChartCard trend={trendData} isPending={trendPending} isError={trendError} />
          </div>
        </section>
      )}

      {/* ── Error state ── */}
      {isError && !isPending && (
        <div className="overview__error" role="alert">
          <p>{STRINGS.error}</p>
          <button type="button" className="overview__retry" onClick={onRetry}>
            {STRINGS.retry}
          </button>
        </div>
      )}

      {/* ── Hospital map — silhouette + beating markers; placed between hero and grid ── */}
      {showMap && (
        <HospitalMap
          hospitals={data?.hospitals}
          isPending={isPending}
          isError={isError}
          onRetry={onRetry}
        />
      )}

      {/* ── Hospital grid ── */}
      {data && !isError && (
        <section className={`hospital-grid${revealClass}`} style={{ animationDelay: "0.16s" }}>
          <header className="hospital-grid__head">
            <p className="hospital-grid__eyebrow">{STRINGS.gridEyebrow}</p>
            <h2 className="hospital-grid__h2">{STRINGS.gridH2}</h2>
            <div className="hospital-grid__toolbar">
              <SortControl value={sort} onChange={onSortChange} />
            </div>
            {/* Freshness from the feed's own reading time, NOT generatedAt (§C). */}
            {data && <FreshnessBadge iso={overviewMeasuredAt(data)} now={now} />}
          </header>
          <ol className="overview__grid" role="list">
            {rankedHospitals.map((ranked, idx) => (
              <li key={ranked.hospital.slug}>
                <HospitalCard ranked={ranked} idx={idx} now={now} />
              </li>
            ))}
          </ol>
        </section>
      )}

      {isPending && <SkeletonGrid />}

      <TriageLegend />
    </main>
  );
}

// ── OverviewPage (connected) ───────────────────────────────────────────────────

/** Read the persisted sort, falling back to the default (and never throwing).
 *  Honours either explicit choice — a stored "asc" must survive a change to the
 *  default, so we can't just test for "desc". */
function readStoredSort(): SortDirection {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    return stored === "asc" || stored === "desc" ? stored : DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
}

export default function OverviewPage() {
  const [sort, setSort] = useState<SortDirection>(readStoredSort);

  const { data, isPending, isError, refetch } = useGetLiveOverview({
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });

  const {
    data: trendData,
    isPending: trendPending,
    isError: trendError,
  } = useGetRegionTrend({
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });

  // Collector health drives the stale-data banner. Same query as the footer
  // indicator (TanStack dedupes), polled on the same cadence.
  const { data: collectorStatus } = useGetCollectorStatus({
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });

  const changeSort = (next: SortDirection) => {
    setSort(next);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, next);
    } catch {
      /* persistence is best-effort; ignore quota/privacy-mode errors. */
    }
  };

  return (
    <OverviewView
      data={data}
      isPending={isPending}
      isError={isError}
      onRetry={() => void refetch()}
      sort={sort}
      onSortChange={changeSort}
      trendData={trendData}
      trendPending={trendPending}
      trendError={trendError}
      collectorStatus={collectorStatus}
    />
  );
}
